<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useFeatureGate } from '@/composables/useFeatureGate'
import SavannaBackground from '@/components/SavannaBackground.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import PageLoading from '@/components/PageLoading.vue'
import PwaManager from '@/components/common/PwaManager.vue'

const authStore = useAuthStore()
const uiStore = useUiStore()
const route = useRoute()
const router = useRouter()
const featureGate = useFeatureGate()

const isDropdownOpen = ref(false)
const isScrolled = ref(false)
const lastScrollY = ref(0)
const isAnyModalOpen = ref(false)
const bottomNavLoadingPath = ref<string | null>(null)
let bottomNavLoadingTimeout: ReturnType<typeof setTimeout> | null = null

// Public commerce routes ship their own customer-facing layout and must not
// inherit the authenticated vendor/super-admin shell navigation.
const publicCommerceRouteNames = new Set([
  'catalog',
  'product-detail',
  'category-products',
  'customer-portal',
  'storefront',
  'storefront-product',
  'storefront-invoice',
  'storefront-service-request',
])

const isPublicCommerceRoute = computed(() =>
  publicCommerceRouteNames.has(String(route.name ?? ''))
)

const showNav = computed(() =>
  authStore.isAuthenticated &&
  !authStore.isCustomer &&
  !isAnyModalOpen.value &&
  !isPublicCommerceRoute.value
)

const vendorAccessLocked = computed(() => false)

const disabledVendorNavClass = computed(() =>
  vendorAccessLocked.value
    ? 'pointer-events-none opacity-45 text-gray-400 hover:bg-transparent hover:text-gray-400'
    : ''
)

const canShowVendorTaxConfig = computed(() => authStore.isAdminVendor && !authStore.isSuperAdmin)
const taxConfigLocked = computed(() => featureGate.loaded.value && featureGate.isLocked('etims'))

const openTaxConfig = async () => {
  isDropdownOpen.value = false
  await router.push(taxConfigLocked.value ? '/subscription?module=module_etims' : '/tax-config')
}

// Watch for modals: any element with z-50+ fixed/absolute overlay
let modalObserver: MutationObserver | null = null
function checkForModals() {
  const modals = document.querySelectorAll('.fixed.inset-0, .fixed.z-50, .fixed.z-\\[60\\]')
  let hasModal = false
  modals.forEach(el => {
    if (el.tagName === 'NAV') return
    const style = window.getComputedStyle(el)
    if (style.display !== 'none' && style.visibility !== 'hidden') {
      hasModal = true
    }
  })
  isAnyModalOpen.value = hasModal
}

const toggleDropdown = (event: Event) => {
  event.stopPropagation()
  isDropdownOpen.value = !isDropdownOpen.value
}

const handleLogout = () => {
  isDropdownOpen.value = false
  authStore.logout()
}

const trackBottomNavNavigation = (path: string) => {
  if (route.path === path) return

  bottomNavLoadingPath.value = path

  if (bottomNavLoadingTimeout) clearTimeout(bottomNavLoadingTimeout)
  bottomNavLoadingTimeout = setTimeout(() => {
    bottomNavLoadingPath.value = null
    bottomNavLoadingTimeout = null
  }, 15000)
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const handleScroll = () => {
  const currentScrollY = window.scrollY
  if (currentScrollY > lastScrollY.value && currentScrollY > 100) {
    isScrolled.value = true
  } else if (currentScrollY < lastScrollY.value || currentScrollY <= 50) {
    isScrolled.value = false
  }
  lastScrollY.value = currentScrollY
}

let scrollTimeout: ReturnType<typeof setTimeout> | null = null
const throttledHandleScroll = () => {
  if (scrollTimeout) return
  scrollTimeout = setTimeout(() => {
    handleScroll()
    scrollTimeout = null
  }, 50)
}

onMounted(() => {
  document.addEventListener('click', closeDropdown)
  window.addEventListener('scroll', throttledHandleScroll, { passive: true })
  modalObserver = new MutationObserver(() => { checkForModals() })
  modalObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  })
  featureGate.fetchGates()
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
  window.removeEventListener('scroll', throttledHandleScroll)
  if (scrollTimeout) clearTimeout(scrollTimeout)
  if (bottomNavLoadingTimeout) clearTimeout(bottomNavLoadingTimeout)
  if (modalObserver) modalObserver.disconnect()
})

watch(() => route.fullPath, () => {
  bottomNavLoadingPath.value = null
  if (bottomNavLoadingTimeout) {
    clearTimeout(bottomNavLoadingTimeout)
    bottomNavLoadingTimeout = null
  }
})

</script>

<template>
  <div
    class="relative min-h-screen"
    :class="isPublicCommerceRoute ? '' : 'bg-gradient-to-br from-amber-50/30 via-white to-emerald-50/20'"
  >
    <ConfirmDialog />
    <PwaManager />
    <PageLoading
      v-if="uiStore.routeLoading"
      :message="uiStore.routeLoadingMessage"
      :overlay="true"
    />
    <SavannaBackground v-if="showNav" />

    <!-- Top Navigation — vendor app only, never on storefront -->
    <nav v-if="showNav" class="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-[70]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center space-x-2 sm:space-x-8">
            <div class="flex items-center space-x-2 sm:space-x-3">
              <div style="height: 48px; width: 140px;">
                <img
                  src="/images/venmas-logo-1.png"
                  alt="Venmas"
                  class="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <!-- User Section -->
          <div class="relative" @click.stop>
            <button
              @click="toggleDropdown"
              class="flex items-center space-x-1 sm:space-x-3 bg-gray-50 hover:bg-gray-100 px-2 sm:px-4 py-2 rounded-lg border border-gray-200 transition-colors duration-200"
            >
              <div class="text-right hidden sm:block">
                <p class="text-sm font-medium text-gray-900 leading-tight">
                  {{ authStore.isSuperAdmin ? 'Super Admin' : (authStore.user?.business_name || 'User') }}
                </p>
                <p class="text-xs text-gray-500 capitalize">
                  {{ authStore.isSuperAdmin ? 'System Administrator' : ((authStore.user?.role || 'user')?.replace('_', ' ')) }}
                </p>
              </div>
              <div
                class="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-r rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base"
                :class="authStore.isSuperAdmin ? 'from-purple-500 to-indigo-600' : 'from-blue-500 to-green-500'"
              >
                {{ authStore.isSuperAdmin ? 'SA' : ((authStore.user?.business_name?.charAt(0) || 'U').toUpperCase()) }}
              </div>
              <svg
                class="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-200 hidden sm:block"
                :class="{ 'rotate-180': isDropdownOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- Dropdown -->
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 transform -translate-y-2"
              enter-to-class="opacity-100 transform translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 transform translate-y-0"
              leave-to-class="opacity-0 transform -translate-y-2"
            >
              <div
                v-if="isDropdownOpen"
                class="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50"
              >
                <!-- Customers -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/customers"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': $route.path === '/customers' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  <span>Customers</span>
                </router-link>

                <!-- Cashiers -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/cashiers"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': $route.path === '/cashiers' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  <span>Cashiers</span>
                </router-link>

                <!-- Reports -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/reports"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': $route.path === '/reports' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                  <span>Reports</span>
                </router-link>

                <!-- Fraud Flags -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/fraud"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-red-50 text-red-700': $route.path === '/fraud' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3.75m0 3.75h.008v.008H12V16.5zM10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  </svg>
                  <span>Fraud Flags</span>
                </router-link>

                <router-link
                  v-if="authStore.isVendor && !authStore.isSuperAdmin"
                  :to="authStore.isSalesVendor ? '/services?tab=requests' : '/services'"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': ($route.path === '/services' || $route.path === '/service-requests') && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"/>
                  </svg>
                  <span>Services</span>
                </router-link>

                <!-- Inventory -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/inventory"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': $route.path === '/inventory' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                  <span>Inventory</span>
                </router-link>

                <!-- Opening/Closing Balances -->
                <router-link
                  v-if="authStore.isVendor && !authStore.isSuperAdmin"
                  to="/business-balances"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-emerald-50 text-emerald-700': $route.path === '/business-balances' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3-.672 3-1.5S13.657 8 12 8z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8c0-1.657 3.134-3 7-3s7 1.343 7 3v8c0 1.657-3.134 3-7 3s-7-1.343-7-3V8z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12c0 1.657 3.134 3 7 3s7-1.343 7-3"/>
                  </svg>
                  <span>Balances</span>
                </router-link>

                <!-- Storefront -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/storefront-manager"
                  @click="isDropdownOpen = false"
                  class="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="[disabledVendorNavClass, { 'bg-blue-50 text-blue-600': $route.path === '/storefront-manager' && !vendorAccessLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9l1.5-4.5A2 2 0 016.4 3h11.2a2 2 0 011.9 1.5L21 9m-18 0v8a2 2 0 002 2h2m12-10v8a2 2 0 01-2 2h-2m-8 0h8m-8 0v-5a1 1 0 011-1h6a1 1 0 011 1v5" />
                  </svg>
                  <span>Storefront</span>
                </router-link>

                <div v-if="authStore.isAdminVendor && !authStore.isSuperAdmin" class="border-t border-gray-100 my-1"></div>

                <!-- Settings -->
                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/settings"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  :class="disabledVendorNavClass"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <span>Settings</span>
                </router-link>

                <!-- Tax Config -->
                <button
                  v-if="canShowVendorTaxConfig"
                  type="button"
                  @click="openTaxConfig"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-150"
                  :class="[{ 'bg-purple-50 text-purple-600': $route.path === '/tax-config' && !taxConfigLocked }, { 'text-gray-400': taxConfigLocked }]"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                  <span>Tax Config</span>
                  <span v-if="taxConfigLocked" class="ml-auto rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">Upgrade</span>
                </button>

                <router-link
                  v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
                  to="/subscription"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-150"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m6-6H6"/></svg>
                  <span>Plans &amp; Modules</span>
                </router-link>

                <div v-if="authStore.isAdminVendor && !authStore.isSuperAdmin" class="border-t border-gray-100 my-1"></div>

                <button
                  @click="handleLogout"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="relative z-10" :class="showNav ? 'pb-20 sm:pb-24' : ''">
      <RouterView />
    </main>

    <!-- Bottom Navigation — vendor app only, never on storefront -->
    <nav
      v-if="showNav && !authStore.isSalesVendor"
      class="fixed bottom-0 left-0 right-0 z-40 sm:bottom-6 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 sm:w-max sm:max-w-[calc(100vw-1rem)]"
      :class="{
        'sm:opacity-0 sm:translate-y-10 sm:pointer-events-none': !isScrolled,
        'sm:opacity-100 sm:translate-y-0': isScrolled
      }"
    >
      <div class="bg-white/95 backdrop-blur-lg shadow-[0_-2px_10px_rgba(0,0,0,0.08)] sm:shadow-2xl border-t border-gray-200/60 sm:border sm:rounded-2xl px-1 sm:px-4 py-1.5 sm:py-3 transition-all duration-500 ease-out">
        <div class="flex items-center justify-around sm:justify-start sm:space-x-1">

          <!-- Super Admin Navigation -->
          <router-link
            v-if="authStore.isSuperAdmin"
            to="/super-admin"
            @click="trackBottomNavNavigation('/super-admin')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-purple-600 bg-purple-50/80"
          >
            <span v-if="bottomNavLoadingPath === '/super-admin'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Dashboard</span>
          </router-link>

          <router-link
            v-if="authStore.isSuperAdmin"
            to="/super-admin/crm"
            @click="trackBottomNavNavigation('/super-admin/crm')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-purple-600 bg-purple-50/80"
          >
            <span v-if="bottomNavLoadingPath === '/super-admin/crm'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h18M5 7v10a2 2 0 002 2h10a2 2 0 002-2V7M8 11h8M8 15h5"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">CRM</span>
          </router-link>

          <router-link
            v-if="authStore.isSuperAdmin"
            to="/super-admin/leads"
            @click="trackBottomNavNavigation('/super-admin/leads')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-purple-600 bg-purple-50/80"
          >
            <span v-if="bottomNavLoadingPath === '/super-admin/leads'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 8l2 2 4-4"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Leads</span>
          </router-link>

          <router-link
            v-if="authStore.isSuperAdmin"
            to="/super-admin/whatsapp-onboarding"
            @click="trackBottomNavNavigation('/super-admin/whatsapp-onboarding')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-purple-600 bg-purple-50/80"
          >
            <span v-if="bottomNavLoadingPath === '/super-admin/whatsapp-onboarding'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 4v-4z"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">QuickStart</span>
          </router-link>

          <router-link
            v-if="authStore.isSuperAdmin"
            to="/super-admin/subscriptions"
            @click="trackBottomNavNavigation('/super-admin/subscriptions')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-purple-600 bg-purple-50/80"
          >
            <span v-if="bottomNavLoadingPath === '/super-admin/subscriptions'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Plans</span>
          </router-link>

          <!-- Admin Vendor Navigation -->
          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/dashboard"
            @click="trackBottomNavNavigation('/dashboard')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-blue-600 bg-blue-50/80"
            :class="disabledVendorNavClass"
          >
            <span v-if="bottomNavLoadingPath === '/dashboard'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Home</span>
          </router-link>

          <!-- Products (covers both Products + Categories tabs) -->
          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/products"
            @click="trackBottomNavNavigation('/products')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-blue-600 bg-blue-50/80"
            :class="disabledVendorNavClass"
          >
            <span v-if="bottomNavLoadingPath === '/products'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Products</span>
          </router-link>

          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/pos"
            @click="trackBottomNavNavigation('/pos')"
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-green-600 hover:bg-green-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 relative"
            active-class="!text-green-600 bg-green-50/80"
            :class="disabledVendorNavClass"
          >
            <div class="w-10 h-10 sm:w-auto sm:h-auto bg-gradient-to-r from-green-500 to-emerald-500 rounded-full sm:rounded-none sm:bg-none flex items-center justify-center -mt-5 sm:mt-0 shadow-lg sm:shadow-none">
              <span v-if="bottomNavLoadingPath === '/pos'" class="h-5 w-5 rounded-full border-2 border-white/60 border-t-white sm:border-green-200 sm:border-t-green-600 animate-spin"></span>
              <svg v-else class="w-5 h-5 text-white sm:text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
            </div>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">POS</span>
          </router-link>

          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/orders"
            @click="trackBottomNavNavigation('/orders')"
            class="relative flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16"
            active-class="!text-blue-600 bg-blue-50/80"
            :class="disabledVendorNavClass"
          >
            <span v-if="bottomNavLoadingPath === '/orders'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"></span>
            <svg v-else class="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            <span class="text-[10px] sm:text-xs font-medium leading-tight mt-0.5">Orders</span>
          </router-link>

          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/payments"
            @click="trackBottomNavNavigation('/payments')"
            class="sm:hidden relative flex flex-col items-center p-2 rounded-lg text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-200 min-w-[3rem]"
            active-class="!text-emerald-600 bg-emerald-50/80"
            :class="disabledVendorNavClass"
          >
            <span v-if="bottomNavLoadingPath === '/payments'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin"></span>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            <span class="text-[10px] font-medium leading-tight mt-0.5">Payments</span>
          </router-link>

          <!-- Desktop-only extra nav item -->
          <router-link
            v-if="authStore.isAdminVendor && !authStore.isSuperAdmin"
            to="/payments"
            @click="trackBottomNavNavigation('/payments')"
            class="hidden sm:flex relative flex-col items-center p-3 rounded-xl text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-200 min-w-16"
            active-class="!text-emerald-600 bg-emerald-50/80"
            :class="disabledVendorNavClass"
          >
            <span v-if="bottomNavLoadingPath === '/payments'" class="mb-0.5 h-5 w-5 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin"></span>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            <span class="text-xs font-medium leading-tight mt-0.5">Payments</span>
          </router-link>

        </div>
      </div>
    </nav>
  </div>
</template>

<style scoped>
* {
  transition: all 0.2s ease-in-out;
}

.fixed nav {
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.9) inset;
}

.router-link-active {
  position: relative;
}

.router-link-active::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: currentColor;
  border-radius: 50%;
}

html {
  scroll-behavior: smooth;
}
</style>
